import React, {
  useCallback, useEffect, useState, useRef,
} from 'react'

import debounce from 'lodash.debounce'

import {
  Search,
  TextInput,
  Button,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, bool, func, string,
} from 'prop-types'


import { sendExperimentEvent } from 'Utils/Experiments'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import Suggestions from './Components/Suggestions'
import ModelSearchTags from './ModelSearchTags'

function ModelSearch({
  placeholder = 'Search',
  searchCallback,
  onKeywordClick,
  tags = EMPTY_ARRAY,
  onDeleteTagClick,
  onClearAllClick,
  extractingCoreStyles,
  isSearchSuggestionList = false,
}) {
  const [suggestions, setSuggestions] = useState()
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searchedValue, setSearchedValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const searchRef = useRef(null)

  const hasTags = tags && tags.length

  const leadIcon = (
    <div className="cgt-model-search__search-icon cgt-icon--dark">
      <Search />
    </div>
  )

  const clearSuggestions = () => {
    setSuggestions()
    setShowSuggestions(false)
  }

  const handleSearchClick = () => {
    setSubmitting(true)
    onKeywordClick({
      suggestedKeyword: searchedValue.toLowerCase(),
      callback: () => setSubmitting(false),
    })
    clearSuggestions()
  }

  const handleClearAllClick = () => {
    setSearchedValue('')
    onClearAllClick()
  }

  const trailButton = (
    <Button onClick={handleSearchClick}>
      {submitting ? (
        <div className="cgt-icon cgt-icon__icon cgt-icon--default cgt-icon--primary">
          <div className="cgt-spinner cgt-spinner--xsmall site-search__loader" />
        </div>
      ) : (
        'Search'
      )}
    </Button>
  )

  const debouncedChange = useCallback(
    debounce(async (value) => {
      const searchResults = await searchCallback(value)
      setSuggestions(searchResults)
      sendExperimentEvent('autocomplete_search_suggestion', searchResults?.autocomplete_search_suggestion)
      return setShowSuggestions(true)
    }, 300),
    []
  )

  async function handleChange(e) {
    e.preventDefault()
    e.stopPropagation()

    const { value } = e.target
    setSearchedValue(value)

    if (value.length > 0) {
      debouncedChange(value)
    } else {
      setSuggestions(null)
      setShowSuggestions(false)
    }
  }

  function handleClick(e) {
    const isSuggestionsClicked = searchRef.current && searchRef.current.contains(e.target)
    if (!isSuggestionsClicked) {
      setShowSuggestions(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick()
      debouncedChange.cancel()
    }

    if (e.key === 'Escape') {
      setShowSuggestions(false)
      debouncedChange.cancel()
    }
  }

  const handleKeywordClick = ({ suggestedKeyword }) => {
    onKeywordClick({ suggestedKeyword })
    setSearchedValue('')
  }

  useEffect(() => {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const keywords = urlParams.get('keywords')
    if (keywords) {
      setSearchedValue(keywords)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  return (
    <div className="cgt-model-search__wrap">
      <div
        className={classnames(
          'cgt-model-search',
          hasTags && 'cgt-model-search--with-tags'
        )}
      >
        <TextInput
          isPilot
          placeholder={placeholder}
          type="primary"
          inputType="text"
          leadIcon={leadIcon}
          trailButton={trailButton}
          handleChange={handleChange}
          onKeyDown={handleKeyDown}
          classNames={classnames(
            'cgt-model-search__input',
            hasTags && 'cgt-model-search__input--with-tags'
          )}
          value={searchedValue}
        />
        {showSuggestions && suggestions?.keywords?.length ? (
          <Suggestions
            suggestions={suggestions}
            onKeywordClick={handleKeywordClick}
          />
        ) : null}
        <ModelSearchTags
          tags={extractingCoreStyles ? ['tag1', 'tag2'] : tags}
          onDeleteTagClick={onDeleteTagClick}
          onClearAllClick={handleClearAllClick}
        />
      </div>
    </div>
  )
}

ModelSearch.propTypes = {
  placeholder: string,
  tags: array,
  searchCallback: func.isRequired,
  onKeywordClick: func.isRequired,
  onDeleteTagClick: func.isRequired,
  onClearAllClick: func.isRequired,
  extractingCoreStyles: bool.isRequired,
  isSearchSuggestionList: bool,
}

export default ModelSearch
